import { Link } from 'wouter';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImage from '../assets/logo.png';

interface NavigationProps {
  currentPath: string;
}

function Logo() {
  return (
    <div className="shrink-0" data-name="Logo">
      <img 
        src={logoImage} 
        alt="Design Interactions Lab" 
        className="w-[100px] h-[56px] object-contain"
      />
    </div>
  );
}

function LogoSmall() {
  return (
    <div className="shrink-0" data-name="LogoSmall">
      <img 
        src={logoImage} 
        alt="Design Interactions Lab" 
        className="w-[54px] h-[32px] object-contain"
      />
    </div>
  );
}

export function Navigation({ currentPath }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/members', label: 'Members' },
    { href: '/publications', label: 'Publications' },
    { href: '/news', label: 'News' },
    { href: '/contact', label: 'Contact' },
  ];

  return (
    <nav className="relative flex justify-center w-full px-6 pt-12 pb-6 z-50">
      <div className="w-full max-w-6xl">
        <div className="bg-white/95 backdrop-blur-sm relative lg:rounded-[100px] rounded-[32px] border border-slate-100 shadow-[0_20px_50px_rgba(79,112,197,0.15)] transition-all duration-500 hover:shadow-[0_25px_60px_rgba(79,112,197,0.2)]">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-between px-10 py-5">
            <Link href="/" aria-label="Home">
              <div className="hover:opacity-80 transition-opacity cursor-pointer">
                <Logo />
              </div>
            </Link>
            
            <div className="relative flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = currentPath === link.href;
                return (
                  <Link key={link.href} href={link.href}>
                    <div className="relative px-5 py-2 cursor-pointer group">
                      <span className={`relative z-10 text-[16px] font-medium transition-colors duration-300 ${
                        isActive ? 'text-[#4f70c5]' : 'text-slate-500 hover:text-slate-900'
                      }`}>
                        {link.label}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Placeholder to balance the logo on the left */}
            <div className="w-[100px]" />
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between px-6 py-4">
              <Link href="/" aria-label="Home">
                <LogoSmall />
              </Link>
              
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2.5 bg-slate-50 rounded-full text-slate-600 hover:text-[#4f70c5] transition-all cursor-pointer active:scale-90"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

            <AnimatePresence>
              {mobileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: 'circOut' }}
                  className="overflow-hidden bg-slate-50/50 rounded-b-[32px] border-t border-slate-100"
                >
                  <div className="px-6 py-8 flex flex-col gap-3">
                    {navLinks.map((link) => {
                      const isActive = currentPath === link.href;
                      return (
                        <Link 
                          key={link.href} 
                          href={link.href}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <div className={`px-6 py-4 rounded-2xl transition-all cursor-pointer text-[17px] ${
                            isActive ? 'bg-white text-[#4f70c5] shadow-sm font-medium' : 'text-slate-500 hover:bg-white/50'
                          }`}>
                            {link.label}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </nav>
  );
}